/**
 * Mounts HubSpot forms that live inside popup dialogs.
 *
 * The embed script renders any .hs-form-frame it finds and keeps watching the
 * DOM for more, so a page with five popup forms would load all five up front.
 * Each popup instead keeps its frame in a <template>, which the embed script
 * cannot see, and this clones it in the first time the popup is opened.
 */

const requestedScripts = new Set();

/**
 * Adds the portal's embed script to the page, once.
 *
 * @param {string} portalId - HubSpot portal (account) ID
 * @param {string} region - HubSpot data centre, e.g. na2
 */
function loadEmbedScript(portalId, region) {
  if (!portalId || !region) return;

  const src = `https://js-${region}.hsforms.net/forms/embed/${portalId}.js`;
  if (requestedScripts.has(src) || document.querySelector(`script[src="${src}"]`)) return;
  requestedScripts.add(src);

  const script = document.createElement('script');
  script.src = src;
  script.defer = true;
  document.head.appendChild(script);
}

/**
 * Moves a dialog's form template into the dialog. Safe to call repeatedly.
 *
 * @param {Element} dialog - The dialog holding the template
 */
function mountForm(dialog) {
  const template = dialog.querySelector('template[data-hs-form]');
  const target = dialog.querySelector('[data-hs-form-target]');

  if (!(template instanceof HTMLTemplateElement) || !target || target.childElementCount > 0) return;

  target.appendChild(template.content.cloneNode(true));

  const frame = target.querySelector('.hs-form-frame');
  if (frame instanceof HTMLElement) loadEmbedScript(frame.dataset.portalId ?? '', frame.dataset.region ?? '');
}

/**
 * Resolves the dialog for a trigger. Help cards wrap the trigger in
 * dialog-component; the PDP quote button lives inside the product form, so
 * its dialog is referenced by id and rendered outside the form.
 *
 * @param {Element} trigger
 * @returns {HTMLDialogElement | null}
 */
function findDialog(trigger) {
  const dialogId = trigger.getAttribute('data-hs-form-dialog');
  if (dialogId) {
    const host = document.getElementById(dialogId);
    const dialog = host?.querySelector('dialog');
    if (dialog instanceof HTMLDialogElement) return dialog;
  }

  const nested = trigger.closest('dialog-component')?.querySelector('dialog');
  return nested instanceof HTMLDialogElement ? nested : null;
}

// Mounting on the trigger's click rather than on dialog:open keeps this
// independent of the dialog component — the form is in place before the dialog
// finishes opening either way.
document.addEventListener('click', (event) => {
  const target = event.target;
  const trigger = target instanceof Element ? target.closest('[data-hs-form-trigger]') : null;
  if (!trigger) return;

  const dialog = findDialog(trigger);
  if (dialog) mountForm(dialog);
});
